// Constants & utils
import { TOOLTIP_STYLE_BAR, TOOLTIP_STYLE_BOX } from 'ChartApp/ts/features/charts-shared/constants';
import { getFormatter } from 'NewAngular/utils/number_formatter';
export default class YchartGeneral {
    constructor(highchartOptions, config) {
        this.categories = [];
        this.chartData = [];
        this.highchartOptions = highchartOptions;
        this.dataLabelsEnabled = false;
        if (highchartOptions.plotOptions.series.dataLabels) {
            this.dataLabelsEnabled = highchartOptions.plotOptions.series.dataLabels.enabled;
        }
        this.config = config;
    }
    chartGeneral(chartInfo) {
        this.chartInfo = chartInfo;
        this.categories = chartInfo.categories;
        this.chartData = chartInfo.chart_data;
        this.config = this.setDefaults();
        this.setupDataSeries();
        this.setupHoverEvents();
        this.setupXAxis();
        this.setupYAxes();
        return this.highchartOptions;
    }
    setDefaults() {
        const config = JSON.parse(JSON.stringify(this.config));
        if (config.hideCategories === undefined) {
            config.hideCategories = false;
        }
        if (config.tooltipStyle === undefined ||
            [TOOLTIP_STYLE_BAR, TOOLTIP_STYLE_BOX].indexOf(config.tooltipStyle) === -1) {
            config.tooltipStyle = TOOLTIP_STYLE_BOX;
        }
        if (config.tooltipCallback === undefined || typeof config.tooltipCallback !== 'function') {
            config.tooltipCallback = undefined;
        }
        if (config.tooltipOverride === undefined || typeof config.tooltipOverride !== 'object') {
            config.tooltipOverride = undefined;
        }
        if (config.xAxisLabelNum === undefined) {
            config.xAxisLabelNum = undefined;
        }
        if (config.yAxisWidth === undefined) {
            config.yAxisWidth = 65;
        }
        return config;
    }
    setupDataSeries() {
        if (this.highchartOptions.series && this.highchartOptions.colors) {
            const seriesTemplate = this.highchartOptions.series[0];
            const numColors = this.highchartOptions.colors.length;
            this.highchartOptions.series = this.chartData.map((chartDataItem, index) => {
                const dataSeries = JSON.parse(JSON.stringify(seriesTemplate));
                dataSeries.name = chartDataItem.label;
                if ('data' in dataSeries) {
                    dataSeries.data = chartDataItem.data;
                }
                if (this.highchartOptions.colors) {
                    dataSeries.color = this.highchartOptions.colors[index % numColors];
                }
                return dataSeries;
            });
        }
    }
    setupHoverEvents() {
        const { tooltipStyle } = this.config;
        if (tooltipStyle === TOOLTIP_STYLE_BAR && this.highchartOptions.plotOptions && this.highchartOptions.tooltip) {
            this.resetDataTooltip();
            // @ts-ignore
            this.highchartOptions.plotOptions.series.point = {
                events: {
                    mouseOver: this.getTooltipHoverHandler(),
                    mouseOut: this.getResetDataTooltipHandler(),
                },
            };
            this.highchartOptions.tooltip.formatter = function () {
                return false;
            };
        }
        else if (tooltipStyle === TOOLTIP_STYLE_BOX && this.highchartOptions.tooltip) {
            this.highchartOptions.tooltip.formatter = this.getTooltipBoxHoverHandler();
        }
    }
    resetDataTooltip() {
        if (this.config.tooltipCallback !== undefined) {
            this.setTooltip(null);
        }
    }
    getResetDataTooltipHandler() {
        const that = this;
        return function () {
            return that.setTooltip(null);
        };
    }
    getTooltipHoverHandler() {
        const that = this;
        return function (event) {
            // @ts-ignore
            const item = { dataIndex: event.target.x };
            return that.setTooltip(item);
        };
    }
    getTooltipBoxHoverHandler() {
        const that = this;
        return function () {
            const idx = that.categories.findIndex((category) => String(category) === String(this.x));
            const item = { dataIndex: idx };
            return that.setTooltip(item);
        };
    }
    setTooltip(item) {
        const tooltipData = [[]];
        // Default to a blank value when outside of plot area
        const dataIndex = item !== null ? item.dataIndex : null;
        const { tooltipOverride, hideCategories } = this.config;
        for (let i = 0; i < this.chartData.length; i += 1) {
            let valFormatted = '';
            if (dataIndex !== null) {
                const chartDataPoint = this.chartData[i].data[dataIndex];
                if (chartDataPoint) {
                    const valFormatter = getFormatter(this.chartData[i].data_type);
                    valFormatted = valFormatter(chartDataPoint);
                }
            }
            let { label } = this.chartData[i];
            if (tooltipOverride && label in tooltipOverride) {
                // @ts-ignore
                label = tooltipOverride[label];
            }
            const tooltip = {
                color: this.highchartOptions.colors ? this.highchartOptions.colors[i] : '',
                colorIndex: i + 1,
                label,
                val: valFormatted,
            };
            // Always add the category unless we explicitly pass in an option to hide it
            if (!hideCategories && dataIndex !== null) {
                tooltip.category = this.categories[dataIndex];
            }
            tooltipData[0].push(tooltip);
        }
        return this.showDataTooltip(tooltipData);
    }
    showDataTooltip(tooltipData) {
        const { tooltipStyle, tooltipCallback } = this.config;
        if (tooltipStyle === TOOLTIP_STYLE_BOX) {
            return this.showDataTooltipBox(tooltipData);
        }
        if (tooltipStyle === TOOLTIP_STYLE_BAR) {
            if (tooltipCallback !== undefined) {
                tooltipCallback(tooltipData);
            }
        }
        return null;
    }
    showDataTooltipBox(tooltipData) {
        let contents = '<div class="chart-column-popup" style="display: block; opacity: 1">';
        let lastValue = '';
        for (let i = 0; i < tooltipData.length; i += 1) {
            for (let j = 0; j < tooltipData[i].length; j += 1) {
                // Draw header
                if (tooltipData[i][j].category && tooltipData[i][j].category !== lastValue) {
                    contents += `<div class="chart-column-title">${tooltipData[i][j].category}</div>`;
                    lastValue = tooltipData[i][j].category;
                }
                // Show label and value if point exists from tooltipData
                if (tooltipData[i][j].val !== '') {
                    contents += '<div class="chart-column-popup-item">';
                    const itemDivClass = `chart-legend-item-title bullet-${tooltipData[i][j].colorIndex}`;
                    // eslint-disable-next-line max-len
                    contents += `<div class="${itemDivClass}"> 
                                    ${tooltipData[i][j].label} 
                                    <span class="chart-column-popup-item-value">${tooltipData[i][j].val}</span>
                                    </div>
                                    </div>`;
                }
            }
        }
        contents += '</div></div>';
        return contents;
    }
    setupXAxis() {
        const { xAxisLabelNum } = this.config;
        if (xAxisLabelNum && this.highchartOptions.xAxis) {
            // Limit the ticks that show by emptying out and use the tickInterval to dictate how they should be drawn
            const divider = this.categories.length - xAxisLabelNum;
            const interval = Math.floor(divider / (xAxisLabelNum - 1)) + 1;
            const mappedCategories = this.categories.map((category, idx) => {
                return idx % interval === 0 ? category : '';
            });
            this.highchartOptions.xAxis.tickInterval = interval;
            // Less padding between columns and add padding on left and right sides
            this.highchartOptions.xAxis.min = -0.5;
            this.highchartOptions.xAxis.max = mappedCategories.length;
        }
        if (this.highchartOptions.xAxis) {
            this.highchartOptions.xAxis.categories = this.categories;
        }
    }
    setupYAxes() {
        const { yAxisWidth } = this.config;
        const yAxes = [];
        const yAxisTypeMap = {};
        let yAxisIdx = 0;
        if (!this.highchartOptions.yAxis || !this.highchartOptions.series)
            return;
        for (let i = 0; i < this.chartData.length; i += 1) {
            const series = this.chartData[i];
            const dataType = series.data_type;
            if (typeof yAxisTypeMap[dataType] !== 'undefined') {
                this.highchartOptions.series[i].yAxis = yAxisTypeMap[dataType];
            }
            else {
                yAxisTypeMap[dataType] = yAxisIdx;
                this.highchartOptions.series[i].yAxis = yAxisTypeMap[dataType];
                // Create new settings for this new y-axis
                const newYAxisOptions = JSON.parse(JSON.stringify(this.highchartOptions.yAxis));
                const labelFormatter = getFormatter(dataType);
                // Create closure to maintain this series reference in formatter function
                if (this.config.partner === 'custom_pdf_report_hypotheticals_contributions_chart') {
                    (function () {
                        if (newYAxisOptions.labels && newYAxisOptions.labels.formatter === undefined) {
                            newYAxisOptions.labels.formatter = function () {
                                return this.value ? `$${labelFormatter(this.value)}` : this.value;
                            };
                        }
                    })();
                }
                else {
                    (function () {
                        if (newYAxisOptions.labels && newYAxisOptions.labels.formatter === undefined) {
                            newYAxisOptions.labels.formatter = function () {
                                return labelFormatter(this.value);
                            };
                        }
                    })();
                }
                // @ts-ignore
                newYAxisOptions.offset = yAxisWidth * yAxisIdx;
                // For additional y-axes, we want there to be a line
                if (yAxisIdx >= 1) {
                    // @ts-ignore
                    newYAxisOptions.lineWidth = 1;
                    // We want Highchart to align the horizontal y-axes so startOnTick must be true
                    yAxes[yAxisIdx - 1].startOnTick = true;
                    // @ts-ignore
                    newYAxisOptions.startOnTick = true;
                }
                yAxes.push(newYAxisOptions);
                yAxisIdx += 1;
            }
        }
        // @ts-ignore
        if ('opposite' in this.highchartOptions.yAxis && this.highchartOptions.yAxis.opposite === false) {
            // @ts-ignore
            this.highchartOptions.chart.marginLeft = yAxes.length * yAxisWidth;
        }
        else {
            // @ts-ignore
            this.highchartOptions.chart.marginRight = yAxes.length * yAxisWidth;
        }
        this.highchartOptions.yAxis = yAxes;
    }
}
